import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, Edit2, RefreshCw, TrendingUp, TrendingDown, Minus, AlertTriangle, FileUp } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import EmptyState from "@/components/shared/EmptyState";
import PdfImportModal from "@/components/ingredients/PdfImportModal";
import TechnicalSheetStatusBadge from "@/components/ingredients/TechnicalSheetStatusBadge";
import TechnicalSheetModal from "@/components/ingredients/TechnicalSheetModal";
import TechnicalSheetDetail from "@/components/ingredients/TechnicalSheetDetail";
import { FileText } from "lucide-react";

const UNITS = ["g", "kg", "ml", "l", "u", "cs", "cc"];

const CATEGORIES = [
  { value: "carns", label: "Carns" },
  { value: "peixos", label: "Peixos i marisc" },
  { value: "lactis", label: "Làctics" },
  { value: "verdures", label: "Verdures i fruita" },
  { value: "cereals", label: "Cereals i farina" },
  { value: "especies", label: "Espècies i condiments" },
  { value: "olis", label: "Olis i greixos" },
  { value: "begudes", label: "Begudes" },
  { value: "altres", label: "Altres" },
];

const CAT_COLORS = {
  carns: "bg-red-100 text-red-700",
  peixos: "bg-blue-100 text-blue-700",
  lactis: "bg-yellow-100 text-yellow-700",
  verdures: "bg-green-100 text-green-700",
  cereals: "bg-amber-100 text-amber-700",
  especies: "bg-purple-100 text-purple-700",
  olis: "bg-orange-100 text-orange-700",
  begudes: "bg-cyan-100 text-cyan-700",
  altres: "bg-gray-100 text-gray-700",
};

export default function IngredientsPage() {
  const { currentVenue } = useVenue();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(null);
  const [recalcResult, setRecalcResult] = useState(null);
  const [form, setForm] = useState({
    name: "", category: "altres", unit: "kg", unit_price: "", supplier: "",
  });
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [technicalSheets, setTechnicalSheets] = useState([]);
  const [sheetModalOpen, setSheetModalOpen] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [filterTechnicalStatus, setFilterTechnicalStatus] = useState("all");

  useEffect(() => {
    if (!currentVenue) return;
    loadIngredients();
    loadTechnicalSheets();
  }, [currentVenue?.id]);

  const loadIngredients = async () => {
    setLoading(true);
    const items = await hosys.entities.Ingredient.filter({ venue_id: currentVenue.id, active: true });
    setIngredients(items.sort((a, b) => a.name.localeCompare(b.name)));
    setLoading(false);
  };

  const loadTechnicalSheets = async () => {
    try {
      const sheets = await hosys.entities.ProductTechnicalSheet.filter({ venue_id: currentVenue.id });
      setTechnicalSheets(sheets);
    } catch (error) {
      console.error("Error loading technical sheets:", error);
    }
  };

  const openForm = (ingredient = null) => {
    if (ingredient) {
      setEditingId(ingredient.id);
      setForm({
        name: ingredient.name,
        category: ingredient.category || "altres",
        unit: ingredient.unit,
        unit_price: ingredient.unit_price.toString(),
        supplier: ingredient.supplier || "",
      });
    } else {
      setEditingId(null);
      setForm({ name: "", category: "altres", unit: "kg", unit_price: "", supplier: "" });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.unit_price) return;
    setSaving(true);
    const existingIng = editingId ? ingredients.find(i => i.id === editingId) : null;
    const newPrice = Number(form.unit_price);
    const data = {
      name: form.name,
      category: form.category,
      unit: form.unit,
      unit_price: newPrice,
      previous_price: existingIng ? existingIng.unit_price : newPrice,
      supplier: form.supplier,
      last_updated: new Date().toISOString().split("T")[0],
    };
    let savedId = editingId;
    if (editingId) {
      await hosys.entities.Ingredient.update(editingId, data);
    } else {
      const created = await hosys.entities.Ingredient.create({ venue_id: currentVenue.id, ...data });
      savedId = created.id;
    }
    setSaving(false);
    setOpenDialog(false);
    await loadIngredients();

    // Auto-recalculate if price changed
    if (editingId && existingIng && existingIng.unit_price !== newPrice) {
      handleRecalculate(savedId);
    }
  };

  const handleDelete = async (id) => {
    {
      await hosys.entities.Ingredient.update(id, { active: false });
      await loadIngredients();
    }
  };

  const handleRecalculate = async (ingredientId) => {
    setRecalculating(ingredientId);
    setRecalcResult(null);
    try {
      const { data } = await hosys.functions.invoke("recalculateRecipeCosts", {
        ingredient_id: ingredientId,
        venue_id: currentVenue.id,
      });
      setRecalcResult({ id: ingredientId, ...data });
    } catch (e) {
      console.error(e);
    } finally {
      setRecalculating(null);
    }
  };

  const getPriceVariation = (ing) => {
    if (!ing.previous_price || ing.previous_price === ing.unit_price) return null;
    const pct = ((ing.unit_price - ing.previous_price) / ing.previous_price) * 100;
    return pct;
  };

  const getSheetForIngredient = (ingredientId) => {
    return technicalSheets.find(s => s.ingredient_id === ingredientId);
  };

  const filtered = ingredients.filter((i) => {
    const matchSearch = i.name.toLowerCase().includes(search.toLowerCase()) ||
      (i.supplier || "").toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCategory === "all" || i.category === filterCategory;
    
    if (filterTechnicalStatus === "all") {
      return matchSearch && matchCat;
    }
    
    const sheet = getSheetForIngredient(i.id);
    if (filterTechnicalStatus === "missing") return !sheet && matchSearch && matchCat;
    if (filterTechnicalStatus === "pending") return sheet?.review_status === "pending_review" && matchSearch && matchCat;
    if (filterTechnicalStatus === "validated") return sheet?.review_status === "validated" && matchSearch && matchCat;
    
    return matchSearch && matchCat;
  });

  if (loading) return <div className="text-center py-8">Carregant...</div>;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Base d'Ingredients"
        subtitle="Gestiona els preus centralitzats dels ingredients per al recàlcul automàtic de costos"
        actions={[
          <Button key="pdf" variant="outline" onClick={() => setPdfModalOpen(true)} className="border-[#7B2D3F]/30 text-[#7B2D3F] hover:bg-[#7B2D3F]/5">
            <FileUp className="w-4 h-4 mr-2" /> Importar PDF
          </Button>,
          <Button key="add" onClick={() => openForm()} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
            <Plus className="w-4 h-4 mr-2" /> Afegir ingredient
          </Button>,
        ]}
      />

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-800">Costos centralitzats</p>
          <p className="text-xs text-blue-600 mt-0.5">
            Quan actualitzeu el preu d'un ingredient, podeu recalcular automàticament el cost de totes les fitxes tècniques que l'utilitzen. El sistema crea alertes si el marge d'algun plat queda per sota del 45%.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          placeholder="Cercar ingredient o proveïdor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="max-w-[200px]">
            <SelectValue placeholder="Totes les categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Totes les categories</SelectItem>
            {CATEGORIES.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterTechnicalStatus} onValueChange={setFilterTechnicalStatus}>
          <SelectTrigger className="max-w-[200px]">
            <SelectValue placeholder="Fitxa tècnica" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Totes</SelectItem>
            <SelectItem value="missing">Sense fitxa</SelectItem>
            <SelectItem value="pending">Pendent revisió</SelectItem>
            <SelectItem value="validated">Validada</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Result notification */}
      {recalcResult && (
        <div className={`rounded-xl border p-4 flex items-start gap-3 ${recalcResult.affectedRecipes > 0 ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`}>
          <RefreshCw className={`w-5 h-5 flex-shrink-0 mt-0.5 ${recalcResult.affectedRecipes > 0 ? "text-emerald-600" : "text-gray-400"}`} />
          <div>
            <p className="text-sm font-medium text-gray-800">Recàlcul completat</p>
            <p className="text-xs text-gray-600 mt-0.5">
              {recalcResult.affectedRecipes} fitxa(es) actualitzada(es).
              {recalcResult.alertsCreated > 0 && ` ${recalcResult.alertsCreated} alerta(es) creada(es) per marge baix.`}
            </p>
            {recalcResult.recipes?.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">Plats: {recalcResult.recipes.map(r => r.name).join(", ")}</p>
            )}
          </div>
          <button onClick={() => setRecalcResult(null)} className="ml-auto text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState title="Cap ingredient" description="Comença afegint els ingredients que uses als teus plats" />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Ingredient</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Categoria</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Fitxa tècnica</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Unitat</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Preu/Unitat</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Variació</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Proveïdor</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Actualitzat</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Accions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((ing) => {
                  const variation = getPriceVariation(ing);
                  const catLabel = CATEGORIES.find(c => c.value === ing.category)?.label || ing.category;
                  return (
                    <tr key={ing.id} className="hover:bg-gray-50 transition-colors">
                       <td className="px-4 py-3 font-medium text-gray-900">{ing.name}</td>
                       <td className="px-4 py-3">
                         {ing.category && (
                           <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CAT_COLORS[ing.category] || "bg-gray-100 text-gray-700"}`}>
                             {catLabel}
                           </span>
                         )}
                       </td>
                       <td className="px-4 py-3">
                         {(() => {
                           const sheet = getSheetForIngredient(ing.id);
                           if (!sheet) {
                             return (
                               <Button
                                 variant="ghost"
                                 size="sm"
                                 onClick={() => {
                                   setSelectedIngredient(ing);
                                   setSheetModalOpen(true);
                                 }}
                                 className="text-blue-600 hover:text-blue-700 text-xs"
                               >
                                 <FileText className="w-3.5 h-3.5 mr-1" /> Afegir
                               </Button>
                             );
                           }
                           return (
                             <button
                               onClick={() => {
                                 setSelectedSheet(sheet);
                                 setDetailModalOpen(true);
                               }}
                               className="cursor-pointer"
                             >
                               <TechnicalSheetStatusBadge
                                 status={sheet.review_status}
                                 source={sheet.source_type}
                               />
                             </button>
                           );
                         })()}
                       </td>
                       <td className="px-4 py-3 text-gray-500">{ing.unit}</td>
                      <td className="px-4 py-3 font-bold text-gray-900">€{ing.unit_price.toFixed(3)}</td>
                      <td className="px-4 py-3">
                        {variation === null ? (
                          <span className="text-gray-300 text-xs flex items-center gap-1"><Minus className="w-3 h-3" /> —</span>
                        ) : variation > 0 ? (
                          <span className={`flex items-center gap-1 text-xs font-semibold ${variation >= 10 ? "text-red-600" : "text-amber-600"}`}>
                            <TrendingUp className="w-3 h-3" /> +{variation.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
                            <TrendingDown className="w-3 h-3" /> {variation.toFixed(1)}%
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{ing.supplier || "—"}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {ing.last_updated ? new Date(ing.last_updated).toLocaleDateString("ca-ES") : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost" size="icon"
                            title="Recalcular fitxes tècniques"
                            onClick={() => handleRecalculate(ing.id)}
                            disabled={recalculating === ing.id}
                            className="text-[#7B2D3F] hover:bg-[#7B2D3F]/10"
                          >
                            {recalculating === ing.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <RefreshCw className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openForm(ing)} className="text-blue-600 hover:text-blue-700">
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(ing.id)} className="text-red-500 hover:text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-400">
            {filtered.length} ingredient(s) · Preu en €/unitat de mesura
          </div>
        </div>
      )}

      {/* PDF Import Modal */}
      <PdfImportModal
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        venueId={currentVenue?.id}
        onImported={() => { setPdfModalOpen(false); loadIngredients(); }}
      />

      {/* Technical Sheet Modal */}
      <TechnicalSheetModal
        open={sheetModalOpen}
        onClose={() => {
          setSheetModalOpen(false);
          setSelectedIngredient(null);
        }}
        onSave={async (data) => {
          if (!selectedIngredient) return;
          await hosys.entities.ProductTechnicalSheet.create({
            venue_id: currentVenue.id,
            ingredient_id: selectedIngredient.id,
            ...data,
            review_status: data.source_type === "internet_ai" ? "pending_review" : "draft",
          });
          await hosys.entities.Ingredient.update(selectedIngredient.id, {
            technical_sheet_status: data.source_type === "internet_ai" ? "ai_generated" : "uploaded",
            technical_sheet_source: data.source_type,
          });
          await loadTechnicalSheets();
          await loadIngredients();
          setSheetModalOpen(false);
          setSelectedIngredient(null);
        }}
        ingredientName={selectedIngredient?.name || ""}
      />

      {/* Technical Sheet Detail Modal */}
      <TechnicalSheetDetail
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedSheet(null);
        }}
        sheet={selectedSheet}
        onUpdate={async (sheetId, data) => {
          await hosys.entities.ProductTechnicalSheet.update(sheetId, data);
          await loadTechnicalSheets();
        }}
        onDelete={async (sheetId) => {
          await hosys.entities.ProductTechnicalSheet.delete(sheetId);
          const ingredient = ingredients.find(i =>
            getSheetForIngredient(i.id)?.id === sheetId
          );
          if (ingredient) {
            await hosys.entities.Ingredient.update(ingredient.id, {
              technical_sheet_status: "missing",
              technical_sheet_source: null,
            });
            await loadIngredients();
          }
          await loadTechnicalSheets();
        }}
      />

      {/* Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar ingredient" : "Afegir ingredient"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input placeholder="Ex: Mozzarella" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Unitat *</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preu/Unitat (€) *</Label>
                <Input type="number" step="0.001" placeholder="0.000" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Proveïdor</Label>
              <Input placeholder="Ex: Proveïdor A" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
            </div>
            {editingId && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                <strong>Nota:</strong> Si canvieu el preu, el sistema recalcularà automàticament el cost de totes les fitxes tècniques que usen aquest ingredient.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Cancel·lar</Button>
            <Button onClick={handleSave} disabled={!form.name || !form.unit_price || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingId ? "Actualitzar" : "Afegir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}